/**
 * Scopes de la consola: el vocabulario con el que las pantallas deshabilitan acciones (`aeRequiresScope`,
 * `<ae-scope-gate>`). El servidor autoriza con los accesos de Sirius `agents_engine_*` (diseño E.3,
 * `src/common/auth/sirius-access.ts` en la raíz); `SCOPE_ACCESS` traduce cada scope al access que lo concede y
 * `scopesOf()` deriva los scopes del `access[]` del JWT. No hay `/auth/me`: la consola decodifica su propio token.
 */
export const API_SCOPES = [
  'catalog:read',
  'catalog:write',
  'agents:publish',
  'runs:start',
  'runs:read',
  'approvals:decide',
  'secrets:write',
  'admin',
] as const;

export type ApiScope = (typeof API_SCOPES)[number];

/** Los ocho accesos del motor (`public.access.name` en Sirius). */
export const SIRIUS_ACCESS = [
  'agents_engine_catalog_read',
  'agents_engine_catalog_write',
  'agents_engine_publish',
  'agents_engine_runs_start',
  'agents_engine_runs_read',
  'agents_engine_approvals_decide',
  'agents_engine_secrets_write',
  'agents_engine_admin',
] as const;

export type SiriusAccess = (typeof SIRIUS_ACCESS)[number];

export const ADMIN_ACCESS: SiriusAccess = 'agents_engine_admin';

/** Scope → access que lo concede (tabla E.3 del diseño; `admin` → `agents_engine_admin`, incluido en el OR de toda ruta). */
export const SCOPE_ACCESS: Readonly<Record<ApiScope, SiriusAccess>> = {
  'catalog:read': 'agents_engine_catalog_read',
  'catalog:write': 'agents_engine_catalog_write',
  'agents:publish': 'agents_engine_publish',
  'runs:start': 'agents_engine_runs_start',
  'runs:read': 'agents_engine_runs_read',
  'approvals:decide': 'agents_engine_approvals_decide',
  'secrets:write': 'agents_engine_secrets_write',
  admin: ADMIN_ACCESS,
};

/** Los scopes que concede un `access[]` de Sirius: `agents_engine_admin` abre todos; si no, uno por access, en el orden de `API_SCOPES`. */
export function scopesOf(access: readonly string[]): ApiScope[] {
  if (access.includes(ADMIN_ACCESS)) return [...API_SCOPES];
  return API_SCOPES.filter(scope => access.includes(SCOPE_ACCESS[scope]));
}

/** `admin` abre todo; varios scopes = basta con uno (misma semántica OR que `EngineAccessGuard`). */
export function hasAnyScope(held: readonly string[], required: readonly ApiScope[]): boolean {
  if (required.length === 0) return true;
  if (held.includes('admin')) return true;
  return required.some(scope => held.includes(scope));
}
